import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  Badge,
  Container,
  useMediaQuery,
  useTheme,
  Slide
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Dashboard,
  Construction,
  Assessment,
  Analytics,
  Info,
  Group,
  Assignment,
  Schedule,
  TaskAlt,
  Description,
  Build,
  Home,
  ContactMail
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import NotificationBell from '../components/common/NotificationBell';

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { notifications } = useNotifications();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const guestNavItems = [
    { text: 'Home', path: '/', icon: <Home /> },
    { text: 'About', path: '/about', icon: <Info /> },
    { text: 'Contact Us', path: '/contact', icon: <ContactMail /> }
  ];

   const supervisorNavItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
    { text: 'Projects', path: '/projects', icon: <Construction /> },
    { text: 'Reports', path: '/reports', icon: <Assessment /> },
    { text: 'Analytics', path: '/analytics', icon: <Analytics /> },
    { text: 'Team Management', path: '/team', icon: <Group /> },
    
  ];

  const technicianNavItems = [
    { text: 'Tasks', path: '/tasks', icon: <Assignment /> },
    { text: 'Assigned Projects', path: '/projects', icon: <Construction /> },
    { text: 'My Reports', path: '/reports', icon: <Description /> },
    { text: 'Equipment', path: '/equipment', icon: <Build /> },
    { text: 'My Team', path: '/my-team', icon: <Group /> },
  ];

   const navItems = !user 
    ? guestNavItems 
    : user.role === 'supervisor' 
      ? supervisorNavItems 
      : technicianNavItems;



  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  console.log(user);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotifOpen = (event) => {
    setNotifAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNotifAnchor(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  useEffect(() => {
    setAnchorEl(null);
    setNotifAnchor(null);
    setMobileOpen(false);
  }, [user]);
  

  return (
    <AppBar position="sticky" elevation={4} sx={{ 
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      zIndex: theme.zIndex.drawer + 1
    }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo/Brand */}
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              mr: 2,
              display: 'flex',
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.2rem',
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            CMS
          </Typography>

          {/* Desktop Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, ml: 3 }}>
            {navItems.map((item) => (
              <Button
                key={item.text}
                startIcon={item.icon}
                component={Link}
                to={item.path}
                sx={{
                  my: 2,
                  mx: 1,
                  color: 'white',
                  fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light
                  }
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>

          {/* User Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
             <NotificationBell />

            {user ? (
              <>
                 <Button
                    color="inherit"
                    startIcon={<AccountCircle sx={{ fontSize: 28 }} />}
                    endIcon={
                      <Avatar 
                        sx={{ width: 32, height: 32, ml: 1 }}
                        // Use email first letter if name isn't available
                        src={user.avatar}
                      >
                        {user.email[0].toUpperCase()}
                      </Avatar>
                    }
                    onClick={handleMenuOpen}
                    sx={{ textTransform: 'none' }}
                  >
                    <Box textAlign="left">
                      {/* Use email instead of name */}
                      <Typography variant="subtitle2">{user.name}</Typography>
                      <Typography variant="caption">{user.role}</Typography>
                    </Box>
                  </Button>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      minWidth: 200,
                      mt: 1.5,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1
                      }
                    }
                  }}
                >
                  <MenuItem onClick={() => handleNavigation('/profile')}>
                    <AccountCircle sx={{ mr: 1.5 }} /> Profile
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => {
                      logout();
                      navigate('/login'); // redirect to login page after logout
                    }}>
                      <Typography color="error">Logout</Typography>
                    </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate('/login')}
                sx={{ 
                  px: 3,
                  borderRadius: 2,
                  boxShadow: theme.shadows[3],
                  '&:hover': {
                    boxShadow: theme.shadows[6]
                  }
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>

        {/* Mobile Navigation Drawer */}
        <Slide direction="down" in={mobileOpen} mountOnEnter unmountOnExit>
          <Box sx={{ 
            background: theme.palette.background.paper,
            boxShadow: theme.shadows[10],
            position: 'absolute',
            left: 0,
            right: 0,
            top: '64px',
            zIndex: theme.zIndex.appBar - 1,
            p: 2
          }}>
            {navItems.map((item) => (
              <Button
                fullWidth
                key={item.text}
                startIcon={item.icon}
                onClick={() => handleNavigation(item.path)} // Changed to onClick
                sx={{
                  justifyContent: 'flex-start',
                  px: 3,
                  my: 0.5,
                  color: location.pathname === item.path ? 
                    theme.palette.primary.main : 'text.primary'
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>
        </Slide>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: 350,
              maxHeight: 400,
              py: 0
            }
          }}
        >
          <Typography variant="subtitle2" sx={{ p: 2, fontWeight: 'bold' }}>
            Notifications ({notifications.length})
          </Typography>
          <Divider />
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <MenuItem key={notification.id} dense>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2">{notification.message}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notification.time}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          ) : (
            <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
              No new notifications
            </Typography>
          )}
        </Menu>
      </Container>
    </AppBar>
  );
};

export default Navbar;